import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';

const Footer = ({ user, page = 'chat' }) => {
  const [footerContent, setFooterContent] = useState(null);
  const [adScripts, setAdScripts] = useState([]);
  const adContainerRef = useRef(null);
  const isPremium = user && user.role === 'premium';

  // Load footer content and ad scripts on mount/page change
  useEffect(() => {
    let mounted = true;

    const fetchData = async () => {
      // Load footer content
      try {
        const footerRes = await axios.get(`${API}/footer/content`);
        if (mounted) setFooterContent(footerRes.data);
      } catch (error) {
        console.error('Failed to load footer content:', error);
      }

      // Load ad scripts (only for non-premium users)
      if (!isPremium) {
        try {
          const pageName = page.toLowerCase() || 'chat';
          const adsRes = await axios.get(`${API}/ad-scripts/${pageName}`);
          if (mounted) {
            const footerAds = (adsRes.data || []).filter(s => s.position === 'footer');
            setAdScripts(footerAds);
          }
        } catch (error) {
          console.error('Failed to load ad scripts:', error);
          if (mounted) setAdScripts([]);
        }
      }
    };

    fetchData();

    return () => {
      mounted = false;
    };
  }, [page, isPremium]);

  // Inject ad scripts when they change
  useEffect(() => {
    const container = adContainerRef.current;
    if (!container || adScripts.length === 0) return;

    // Clear and inject
    container.innerHTML = '';

    adScripts.forEach((script, index) => {
      try {
        const wrapper = document.createElement('div');
        wrapper.className = 'ad-script-wrapper';
        wrapper.id = `ad-script-${index}`;

        const scriptContent = script.script_code;

        if (scriptContent.includes('<script')) {
          const temp = document.createElement('div');
          temp.innerHTML = scriptContent;

          temp.querySelectorAll('script').forEach(oldScript => {
            const newScript = document.createElement('script');
            Array.from(oldScript.attributes).forEach(attr => {
              newScript.setAttribute(attr.name, attr.value);
            });
            if (oldScript.innerHTML) {
              newScript.textContent = oldScript.innerHTML;
            }
            wrapper.appendChild(newScript);
          });

          const htmlWithoutScripts = scriptContent.replace(/<script[\s\S]*?<\/script>/gi, '');
          if (htmlWithoutScripts.trim()) {
            const contentDiv = document.createElement('div');
            contentDiv.innerHTML = htmlWithoutScripts;
            wrapper.insertBefore(contentDiv, wrapper.firstChild);
          }
        } else {
          wrapper.innerHTML = scriptContent;
        }

        container.appendChild(wrapper);
      } catch (error) {
        console.error(`Failed to inject ad script ${index}:`, error);
      }
    });

    return () => {
      if (container) container.innerHTML = '';
    };
  }, [adScripts]);

  // Don't show footer for premium users
  if (isPremium) {
    return null;
  }

  return (
    <footer className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 mt-auto">
      <div className="container mx-auto px-4">
        {/* Ad Scripts Container */}
        {adScripts.length > 0 && (
          <div className="mb-3 text-center">
            <div
              ref={adContainerRef}
              className="ad-container bg-white/10 backdrop-blur-sm rounded-md py-2 px-3 border border-white/20"
            />
          </div>
        )}

        {/* Footer Content */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-3 text-sm">
          <div className="flex flex-wrap gap-4 justify-center md:justify-start">
            {footerContent?.links?.map((link, index) => (
              <div key={index} className="flex items-center gap-4">
                {index > 0 && <span className="text-blue-100/50">|</span>}
                <a href={link.url} className="text-blue-100 hover:text-white transition-colors">
                  {link.label}
                </a>
              </div>
            )) || (
              <>
                <Link to="/about" className="text-blue-100 hover:text-white transition-colors">About</Link>
                <span className="text-blue-100/50">|</span>
                <Link to="/premium" className="text-blue-100 hover:text-white transition-colors">Premium</Link>
                <span className="text-blue-100/50">|</span>
                <Link to="/feedback" className="text-blue-100 hover:text-white transition-colors">Contact</Link>
                <span className="text-blue-100/50">|</span>
    
                <span className="text-blue-100/50">|</span>
                <Link to="/auth" className="text-blue-100 hover:text-white transition-colors">Sign In</Link>
              </>
            )}
            <span className="text-blue-100/50 mx-2">|</span>
            <Link to="/privacy-policy" className="text-blue-100 hover:text-white transition-colors">Privacy Policy</Link>
            <span className="text-blue-100/50 mx-2">|</span>
            <Link to="/terms-of-service" className="text-blue-100 hover:text-white transition-colors">Terms of Service</Link>
          </div>

          <div className="text-center md:text-right">
            <p className="text-blue-100 text-xs">
              {footerContent?.copyright_text || `© ${new Date().getFullYear()} ChatHub. All rights reserved.`}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
